import React from 'react';
import CasinoTournament from 'app/components/pages/casino/marketing-blocks/CasinoTournament';
import CasinoLootboxBlock from 'app/components/pages/casino/marketing-blocks/CasinoLootboxBlock';
import CasinoNewReleaseBlock from 'app/components/pages/casino/marketing-blocks/CasinoNewReleaseBlock';
import CasinoPromotionsBlock from 'app/components/pages/casino/marketing-blocks/CasinoPromotionsBlock';
import {CONTENT_TYPE, LAYOUT} from 'app/components/pages/casino/marketing-blocks/CasinoMarketingUtils';
import type {
    BlockConfig,
    PageElementsBuildOptions
} from 'app/components/pages/casino/marketing-blocks/CasinoMarketingBlocksTypes';
import type {PageElementInterface} from 'app/interfaces/stores/CasinoGamesStoreInterfaces';

interface BaseBlockParams {
    blockSize: string;
    rows: number;
    visible: boolean;
}

const createBlock = (
    contentType: string,
    element: React.ReactNode,
    params: BaseBlockParams
): BlockConfig => ({
    blockSize: params.blockSize,
    contentType,
    element,
    rows: params.rows,
    visible: params.visible
});

const createTournamentBlock = (
    element: PageElementInterface,
    opts: PageElementsBuildOptions,
    base: BaseBlockParams
): BlockConfig => createBlock(
    CONTENT_TYPE.tournament,
    <CasinoTournament
        bannerImageUrl={element.banner_image_url}
        promotionSlug={element.promotion_slug}
        promotionEndTime={element.promotion_end_time}
        onExpire={opts.tournament.onTournamentExpire}
        filterGroupId={element.filter_group_id}
        onGoToTournamentGames={opts.tournament.onGoToTournamentGames}
    />,
    {...base, visible: base.visible && !opts.tournament.isTournamentExpired}
);

const createLootboxBlock = (
    element: PageElementInterface,
    opts: PageElementsBuildOptions,
    base: BaseBlockParams
): BlockConfig => createBlock(
    CONTENT_TYPE.lootboxes,
    <CasinoLootboxBlock
        lootboxes={opts.lootbox.items}
        onUpdateShop={opts.lootbox.onUpdateShop}
        scrollToFilter={opts.lootbox.scrollToFilter}
    />,
    {...base, visible: base.visible && opts.lootbox.items.length > 0}
);

const createPromotionsBlock = (
    element: PageElementInterface,
    opts: PageElementsBuildOptions,
    base: BaseBlockParams
): BlockConfig => createBlock(
    CONTENT_TYPE.promotions,
    <CasinoPromotionsBlock promotions={opts.promotions} />,
    {...base, visible: base.visible && opts.promotions.length > 0}
);

const createNewReleaseBlock = (
    element: PageElementInterface,
    opts: PageElementsBuildOptions,
    base: BaseBlockParams
): BlockConfig => createBlock(
    element.content_type,
    <CasinoNewReleaseBlock
        activeClickGame={opts.newRelease.activeClickGame}
        cool_off_present={opts.newRelease.cool_off_present}
        deactivateGame={opts.newRelease.deactivateGame}
        game={element.casino_game}
        isGameOfTheWeek={element.content_type === CONTENT_TYPE.gameOfWeek}
        setActiveGame={opts.newRelease.setActiveGame}
    />,
    base
);

type BlockFactory = (
    element: PageElementInterface,
    opts: PageElementsBuildOptions,
    base: BaseBlockParams
) => BlockConfig;

const BLOCK_FACTORIES: Partial<Record<string, BlockFactory>> = {
    [CONTENT_TYPE.tournament]: createTournamentBlock,
    [CONTENT_TYPE.lootboxes]: createLootboxBlock,
    [CONTENT_TYPE.promotions]: createPromotionsBlock,
    [CONTENT_TYPE.newRelease]: createNewReleaseBlock,
    [CONTENT_TYPE.gameOfWeek]: createNewReleaseBlock
};

export const createBlockConfig = (
    element: PageElementInterface,
    opts: PageElementsBuildOptions
): BlockConfig | null => {
    const base: BaseBlockParams = {
        blockSize: element.block_size,
        rows: element.block_size === 'large' ? opts.layout.blockRows : LAYOUT.marketingBlockRows,
        visible: Boolean(element?.active)
    };

    const factory = BLOCK_FACTORIES[element.content_type];

    return factory ? factory(element, opts, base) : null;
};
